import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, getAuthHeaders } from "../api";
import { describeDropFlags } from "../lib/extractionReport";
import ExpenseStatement from "./ExpenseStatement";
import {
  CASE_STATUS_LABELS,
  parseExtractionConfidence,
  type Case,
  type CaseLink,
  type CaseRecord,
  type CaseStatus,
  type CaseVolume,
  type Contact,
  type FindingActionKind,
} from "../types";
import {
  qaSections,
  qaUnaddressedCount,
  withAction,
  type QaSection,
} from "../lib/findings";
import QaFindingsPanel from "./ui/QaFindingsPanel";
import Timeline from "./Timeline";
import Interrogate, { type InterrogateScope } from "./Interrogate";
import AddMenu, { type AddMode } from "./AddMenu";
import CaptureOverlay from "./CaptureOverlay";
import PasteCanvas from "./PasteCanvas";
import RecordCall from "./RecordCall";
import FileUploadPanel from "./FileUploadPanel";
import ContactsTab from "./ContactsTab";
import FilesTab from "./FilesTab";
import StructuredOverview from "./StructuredOverview";
import ReimbursementSummaryView from "./ReimbursementSummaryView";
import BackToSummaryBar, { shouldShowBackToSummary } from "./BackToSummaryBar";
import BackBar from "./BackBar";
import {
  shouldShowRecordBack,
  recordBackLabel,
  type RecordReturn,
} from "../lib/recordReturn";
import LinkedCases from "./LinkedCases";
import ShareManager from "./ShareManager";
import RepairDatesModal from "./RepairDatesModal";
import WriteRecordModal from "./WriteRecordModal";
import KeyFactsPanel from "./KeyFactsPanel";
import CostsPanel from "./CostsPanel";
import VolumeBanner from "./VolumeBanner";
import TimelineTab from "./TimelineTab";
import ModalBackdrop from "./ui/ModalBackdrop";
import { useModalEscape } from "../lib/useModalEscape";
import {
  loadShowRoutine,
  loadTimelineSubView,
  saveShowRoutine,
  saveTimelineSubView,
  type TimelineSubView,
} from "../lib/viewModeStore";
import { exportCaseDossierPdf } from "../lib/exportDossier";
import { exportCaseAsDocx } from "../lib/exportDocx";
import { buildStatusParagraph, computeStaleness } from "../lib/exportShared";
import { formatDate, formatDateTime } from "../lib/formatDate";
import { renderProseFromEvents } from "../lib/renderProseFromEvents";
import ExportProgressModal, {
  type ExportProgressState,
} from "./ExportProgressModal";
import { exportCaseAsXlsx } from "../lib/exportXlsx";
import { exportCaseAsZip } from "../lib/exportZip";
import {
  exportExpenseReportZip,
  hasExpenseData,
} from "../lib/exportExpenseReport";
import { exportTimelinePdf } from "../lib/exportTimeline";
import { timelineRecordsToEvents } from "../lib/timelineAdapter";
import { buildCaseOverview } from "../lib/caseOverview";
import { buildPartyIndex } from "../lib/parties";
import type { CaseOverview } from "../lib/caseOverview";
import type { VolumeBriefEntry } from "../lib/exportShared";
import type { TimelineEvent } from "../types";
import { Button } from "./ui/Button";
import { Dropdown } from "./ui/Dropdown";
import Tooltip from "./ui/Tooltip";
import { useIsMobile } from "../lib/useBreakpoint";

interface Props {
  caseId: string;
  allCases: Case[];
  onDeleted: (id: string) => void;
  onCaseChanged: () => void;
  onCaseUpdated: (c: Case) => void;
  onNavigateCase: (id: string) => void;
  // Volume state is lifted to AuthenticatedApp so the Sidebar can
  // render the multi-volume tree + kebab menu for the active case.
  // CaseView still owns the per-case fetch; it mirrors the results
  // up via onVolumesChanged so the sidebar can build its tree.
  onVolumesChanged?: (volumes: CaseVolume[]) => void;
  // Imperative refresh hook — AuthenticatedApp invokes a volume
  // action (close / rename) and then calls this to ask CaseView to
  // refresh its records + volumes without re-mounting.
  onRegisterRefresh?: (fn: () => Promise<void>) => void;
  // Imperative tab-switch hook — AuthenticatedApp uses this to flip
  // CaseView's active tab in response to sidebar menu actions
  // ("Ask AI about this volume" → interrogate, "View summary" →
  // records). Cleaner than the previous tick-signal+useRef
  // comparison, which was subtle to reason about and fragile
  // under StrictMode double-mount.
  onRegisterTabSetter?: (fn: (tab: CaseViewTab) => void) => void;
  // Controlled prop from AuthenticatedApp. When non-null, Timeline
  // force-expands that volume's section in the Records tab and
  // scrolls the summary into view, then calls onShowSummaryHandled
  // to clear it back to null. Owned by App so the sidebar kebab's
  // "View summary" handler can set it declaratively.
  showSummaryVolumeId?: string | null;
  onShowSummaryHandled?: () => void;
  // Imperative setter for the Interrogate scope. Called by
  // AuthenticatedApp when the sidebar kebab fires "Ask AI about
  // this volume" — CaseView switches the tab + applies the scope
  // locally (see the Interrogate scope state below). Values: "case"
  // or a volume id string.
  onRegisterSetInterrogateScope?: (
    fn: (scope: InterrogateScope) => void,
  ) => void;
}

export type CaseViewTab =
  | "overview"
  | "records"
  | "timeline"
  | "summary"
  | "files"
  | "contacts"
  | "interrogate";

type Tab = CaseViewTab;

const OVERLAY_TITLES: Record<AddMode, string> = {
  paste: "Paste screenshot or text",
  receipt: "Scan a receipt",
  file: "Upload a file",
  record: "Record a call",
  audio: "Upload an audio file",
  // "write" isn't rendered via CaptureOverlay — the WriteRecordModal
  // owns its own chrome — but the Record<AddMode, string> type needs
  // an entry so TypeScript doesn't complain.
  write: "Write record",
};

const STATUS_OPTIONS: CaseStatus[] = [
  "open",
  "waiting",
  "escalated",
  "resolved",
];

function formatLongDate(iso: string): string {
  // Route through the shared utility so deadlines and case-level
  // dates follow the same UTC formatting as every other date in the
  // app.
  return formatDate(iso, "long") || iso;
}

function deadlineVariant(
  deadline: string | null,
): "none" | "future" | "near" | "past" {
  if (!deadline) return "none";
  const d = new Date(deadline + "T23:59:59").getTime();
  const now = Date.now();
  if (d < now) return "past";
  if (d - now < 3 * 24 * 60 * 60 * 1000) return "near";
  return "future";
}

function relativeDays(iso: string): { label: string; days: number } {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { label: "—", days: Infinity };
  const diffDays = Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
  if (diffDays <= 0) return { label: "today", days: 0 };
  if (diffDays === 1) return { label: "yesterday", days: 1 };
  if (diffDays < 7) return { label: `${diffDays} days ago`, days: diffDays };
  if (diffDays < 30) {
    const wk = Math.floor(diffDays / 7);
    return { label: `${wk} week${wk === 1 ? "" : "s"} ago`, days: diffDays };
  }
  return { label: relativeMonthLabel(diffDays), days: diffDays };
}
function relativeMonthLabel(days: number): string {
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

export default function CaseView({
  caseId,
  allCases,
  onDeleted,
  onCaseChanged,
  onCaseUpdated,
  onNavigateCase,
  onVolumesChanged,
  onRegisterRefresh,
  onRegisterTabSetter,
  showSummaryVolumeId = null,
  onShowSummaryHandled,
  onRegisterSetInterrogateScope,
}: Props) {
  const isMobile = useIsMobile();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [records, setRecords] = useState<CaseRecord[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [links, setLinks] = useState<CaseLink[]>([]);
  // Volumes drive the TrialBanner threshold prompt, the Timeline
  // volume headers, the Interrogate context indicator, and the
  // Settings volume-management section. A single-volume case still
  // fetches this list, but all the downstream UI branches suppress
  // themselves until there's more than one volume (or until the
  // sole volume crosses the prompt threshold).
  const [volumes, setVolumes] = useState<CaseVolume[]>([]);
  // Interrogate scope — "case" (entire-case default) or a volume id
  // (drill-back into that volume). Owned here rather than inside
  // Interrogate so external triggers (sidebar "Ask AI about this
  // volume", Timeline's per-volume summary button) can push scope
  // changes without racing Interrogate's mount cycle.
  const [interrogateScope, setInterrogateScope] =
    useState<InterrogateScope>("case");
  // When the user clicks "Help me do this" on the Suggested Action
  // card, we switch to the Interrogate tab and hand the composed
  // prompt to Interrogate via this prop. Interrogate consumes once
  // and calls back to clear, so re-entering the tab doesn't replay.
  const [pendingInterrogatePrompt, setPendingInterrogatePrompt] = useState<
    string | null
  >(null);
  // One-shot "scroll and expand this record" target, driven by AI
  // citation clicks and the Repair dates modal's "View record" link.
  // The nonce ensures every request has a unique object identity
  // so Timeline's scroll effect fires even when the same record id
  // is requested twice in a row.
  const [scrollToRecordId, setScrollToRecordId] = useState<{
    id: string;
    n: number;
    attachmentId?: string | null;
  } | null>(null);
  // One-shot "return to this summary row" target. Lifted to CaseView (which
  // stays mounted) because the Summary tab unmounts when the user jumps to
  // Records to view a source; on Back, the Summary remounts and scrolls to
  // this row + flashes it, so the user keeps their place in a flagged list.
  const [summaryFocusRow, setSummaryFocusRow] = useState<{
    key: string;
    n: number;
  } | null>(null);
  // App-wide cross-tab return target: when a record is opened from an origin
  // whose tab UNMOUNTS (Overview, Files, Timeline visual, Interrogate deep),
  // this records where to go back to. The shared BackBar on the Records tab
  // reads it. Financials keeps its own `summaryFocusRow` (untouched) and never
  // sets this — the render gate excludes the case where both are set.
  const [recordReturn, setRecordReturn] = useState<RecordReturn | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  // Timeline tab hosts two sub-views: the Visual infographic and the
  // Written narrative (the latter absorbed the retired Story tab).
  // Seeded from localStorage so the user's choice survives reloads
  // and case switches.
  const [timelineSubView, setTimelineSubViewState] =
    useState<TimelineSubView>(() => loadTimelineSubView(caseId));
  useEffect(() => {
    setTimelineSubViewState(loadTimelineSubView(caseId));
  }, [caseId]);
  const setTimelineSubView = useCallback(
    (sv: TimelineSubView) => {
      setTimelineSubViewState(sv);
      saveTimelineSubView(caseId, sv);
    },
    [caseId],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addMode, setAddMode] = useState<AddMode | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [bulkBusy, setBulkBusy] = useState<
    null | "thread" | "unthread" | "audit" | "facts" | "volstats"
  >(null);
  // Bumped after a batch fact extraction so the Overview refetches facts.
  const [factsNonce, setFactsNonce] = useState(0);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [repairDatesOpen, setRepairDatesOpen] = useState(false);
  // Progress modal for PDF/DOCX exports. Null when no export is in
  // flight; a partial state during the staleness + regeneration
  // phases; cleared when the export download triggers.
  const [exportProgress, setExportProgress] =
    useState<ExportProgressState | null>(null);
  // The dossier/brief are now fully deterministic — assembled from verified
  // facts with no AI prose — so there is no validation to gate on or display.
  // When true, the Records tab filters to records whose date came
  // from the "fallback" pathway (no date in content). Triggered by
  // the Data-quality stat in the case header and cleared by the
  // Timeline's in-list "Clear filter" button.
  const [onlyFallbackDates, setOnlyFallbackDates] = useState(false);
  // QA findings panel (Round 10.1): the case's stored validation scans,
  // lifted to state so dismiss/accept updates the needs-attention count
  // live. Re-seeded whenever the case row's blobs change.
  const [qaValidations, setQaValidations] = useState<QaSection[]>([]);
  const [qaPanelOpen, setQaPanelOpen] = useState(false);
  useEffect(() => {
    setQaValidations(
      qaSections(caseData?.summary_validation, caseData?.costs_validation),
    );
  }, [caseData?.id, caseData?.summary_validation, caseData?.costs_validation]);
  const onQaAction = useCallback(
    (
      target: QaSection["target"],
      findingId: string,
      kind: FindingActionKind,
      note?: string,
    ) => {
      setQaValidations((prev) =>
        prev.map((s) => {
          if (s.target !== target) return s;
          const nextActions = withAction(
            s.validation.actions ?? [],
            findingId,
            kind,
            note,
          );
          if (caseData)
            void api
              .saveValidationActions(caseData.id, target, nextActions)
              .catch(() => {});
          return { ...s, validation: { ...s.validation, actions: nextActions } };
        }),
      );
    },
    [caseData?.id],
  );
  // "Show routine" lives here (not inside Timeline) so the Records
  // tab label can report the visible count in lockstep with the
  // toggle. Persists per case in localStorage; re-initialises when
  // the active case changes.
  const [showRoutine, setShowRoutineState] = useState<boolean>(() =>
    loadShowRoutine(caseId),
  );
  useEffect(() => {
    setShowRoutineState(loadShowRoutine(caseId));
  }, [caseId]);
  const setShowRoutine = useCallback(
    (v: boolean) => {
      setShowRoutineState(v);
      saveShowRoutine(caseId, v);
    },
    [caseId],
  );
  const [slugEditorOpen, setSlugEditorOpen] = useState(false);
  const [autoScoreStatus, setAutoScoreStatus] = useState<
    null | "scoring" | "keyfacts" | "done"
  >(null);
  // After auto-scoring runs, this holds the count of records the AI
  // freshly classified as 'low' (and which the routine filter is
  // therefore now hiding). Drives a one-shot toast with a Show link
  // so the user isn't blindsided by their record vanishing. Cleared
  // on its own timer or when the user toggles routine on.
  const [recentlyHiddenCount, setRecentlyHiddenCount] = useState<
    number | null
  >(null);
  // Track which cases we've already auto-scored this session so the
  // effect doesn't refire on every refresh and burn through the
  // batch_ai rate limit.
  const autoScoredCasesRef = useRef<Set<string>>(new Set());
  // Mirrors showRoutine into a ref so the auto-score async closure
  // can read the fresh value without re-running the whole effect on
  // every toggle change.
  const showRoutineRef = useRef(showRoutine);
  useEffect(() => {
    showRoutineRef.current = showRoutine;
  }, [showRoutine]);
  // Running total of rows applied inside the Repair dates modal
  // across its open session. Used by the close/view-record handlers
  // to post a single summary toast and trigger one refresh, rather
  // than doing it after every row.
  const repairAppliedTotalRef = useRef<number>(0);

  // Light-weight re-fetch of just the volume list. Called after the
  // user closes a volume (from VolumeBanner or Settings) so the
  // Timeline headers + context indicator update without re-loading
  // every record. The full records list is separately re-fetched by
  // the existing refresh() below.
  const refreshVolumes = useCallback(async () => {
    try {
      const { volumes } = await api.listVolumes(caseId);
      setVolumes(volumes);
    } catch {
      /* volume list is advisory UI — swallow errors quietly */
    }
  }, [caseId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { case: c },
        { records },
        { contacts },
        { links },
        { volumes },
      ] = await Promise.all([
        api.getCase(caseId),
        api.listRecords(caseId),
        api.listContacts(caseId),
        api.listLinks(caseId),
        api.listVolumes(caseId),
      ]);
      setCaseData(c);
      setRecords(records);
      setContacts(contacts);
      setLinks(links);
      setVolumes(volumes);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Mirror volumes up to AuthenticatedApp so the Sidebar can render
  // the volume tree for the active case. Fires on mount + whenever
  // the volume list changes (close / rename / new volume creation).
  useEffect(() => {
    onVolumesChanged?.(volumes);
  }, [volumes, onVolumesChanged]);

  // Register an imperative refresh callback with the parent so
  // AuthenticatedApp can trigger a refetch after sidebar-initiated
  // volume actions (close / rename) without re-mounting the case
  // view. The dependency on `refresh` + `refreshVolumes` ensures the
  // latest closures land in the parent ref.
  useEffect(() => {
    if (!onRegisterRefresh) return;
    onRegisterRefresh(async () => {
      await refreshVolumes();
      await refresh();
    });
  }, [onRegisterRefresh, refresh, refreshVolumes]);

  // Expose setTab to AuthenticatedApp so sidebar menu actions can
  // flip the tab directly. Registered on mount + re-registered if
  // onRegisterTabSetter's identity ever changes (unlikely, but
  // harmless). setTab is stable (React state setter) so the handler
  // the parent stores is always current.
  useEffect(() => {
    if (!onRegisterTabSetter) return;
    onRegisterTabSetter(setTab);
  }, [onRegisterTabSetter]);

  // Register the Interrogate scope setter with the parent. Sidebar's
  // "Ask AI about this volume" calls it with the volume id; the
  // scope drives both the selector state and the endpoint dispatch
  // inside Interrogate.
  useEffect(() => {
    if (!onRegisterSetInterrogateScope) return;
    onRegisterSetInterrogateScope(setInterrogateScope);
  }, [onRegisterSetInterrogateScope]);

  // Auto-score: once records are loaded, if EVERY record is still at
  // the default 'medium' with no significance_reason, it's never been
  // scored. Fire /score in the background then /key-facts/refresh if
  // the panel is empty. Runs at most once per (caseId, session).
  useEffect(() => {
    if (!caseData || records.length === 0) return;
    if (autoScoredCasesRef.current.has(caseData.id)) return;

    // Fire if any record has no significance_reason — that's our
    // "never been scored" marker. Previously we required EVERY record
    // to be unscored, which meant newly-arrived records on a partially
    // scored case stayed unscored until manual action.
    const anyUnscored = records.some((r) => !r.significance_reason);
    if (!anyUnscored) return;

    autoScoredCasesRef.current.add(caseData.id);
    const caseId = caseData.id;
    const hasKeyFactsCache = !!caseData.key_facts;
    // Snapshot the count of low-significance records BEFORE scoring
    // so we can detect whether scoring just hid any new ones. Used
    // below to drive the "N records hidden" toast.
    const lowBefore = records.filter(
      (r) => r.significance === "low",
    ).length;

    (async () => {
      setAutoScoreStatus("scoring");
      try {
        await api.scoreAll(caseId);
      } catch (err) {
        // Rate-limited or down — don't badger the user. The manual
        // "Score records" menu item in the Maintain dropdown stays
        // available for retry.
        console.warn("auto-score failed", err);
        setAutoScoreStatus(null);
        return;
      }
      if (!hasKeyFactsCache) {
        setAutoScoreStatus("keyfacts");
        try {
          await api.refreshKeyFacts(caseId);
        } catch (err) {
          console.warn("auto key-facts failed", err);
        }
      }
      setAutoScoreStatus("done");
      await refresh();

      // Compute the "newly hidden by routine filter" count. We need
      // a direct fetch here because refresh() updated state but the
      // async closure's `records` reference is still stale, and the
      // useEffect that mirrors records → ref hasn't fired yet.
      try {
        const { records: scored } = await api.listRecords(caseId);
        const lowAfter = scored.filter(
          (r) => r.significance === "low",
        ).length;
        const newlyHidden = Math.max(0, lowAfter - lowBefore);
        if (newlyHidden > 0 && !showRoutineRef.current) {
          setRecentlyHiddenCount(newlyHidden);
          // Auto-dismiss after 8s — long enough to read and act on,
          // short enough not to clutter the case view permanently.
          window.setTimeout(() => setRecentlyHiddenCount(null), 8000);
        }
      } catch {
        /* comparison toast is non-essential; swallow and move on */
      }

      // Let the "done" state linger briefly so the user sees it
      // succeeded, then clear.
      window.setTimeout(() => setAutoScoreStatus(null), 2000);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.id, records.length]);

  const handleRecordCreated = useCallback(
    (r: CaseRecord) => {
      setRecords((prev) =>
        [...prev, r].sort((a, b) =>
          a.record_date > b.record_date
            ? -1
            : a.record_date < b.record_date
              ? 1
              : 0,
        ),
      );
      setTab("records");
    },
    [],
  );

  // Shared handler for AI-citation clicks and the Repair dates
  // modal's "View record ↗" button. Swaps to the Records tab and
  // sets the one-shot scroll target; Timeline consumes the id,
  // expands + scrolls that record into view, then clears it via
  // onScrollToRecordHandled.
  //
  // Using the updater form + a ticking nonce means every click
  // produces a fresh object reference even when the same record id
  // is requested twice. A plain string state would let React bail
  // on the second click (Object.is match) and the scroll effect
  // would never fire.
  const navigateToRecord = useCallback(
    (
      recordId: string,
      attachmentId?: string | null,
      // When the caller's origin lives in a tab that unmounts on this jump,
      // it passes where to return to. The shared BackBar then offers the way
      // back. Omitted by same-tab / in-place callers (e.g. Financials, which
      // drives its own summaryFocusRow return).
      origin?: RecordReturn,
    ) => {
      setRecordReturn(origin ?? null);
      setTab("records");
      setScrollToRecordId((prev) => ({
        id: recordId,
        n: (prev?.n ?? 0) + 1,
        attachmentId: attachmentId ?? null,
      }));
    },
    [],
  );

  // Browser-history integration for tab changes and modal opens.
  //
  // `popstateRestoringRef` guards against ping-pong: when popstate
  // fires, we set state from the entry's payload, which triggers
  // the "push on change" effect below. Without the guard, that
  // effect would then push a NEW history entry that shadows the one
  // the user just came back to.
  const popstateRestoringRef = useRef(false);
  const historyInitialisedRef = useRef(false);

  interface CaseHistoryState {
    kind: "case-view";
    // Legacy "story" entries from before the Story → Timeline merge
    // still exist in users' browser histories; the popstate handler
    // below coerces them to the new shape.
    tab: Tab | "story";
    timelineSubView?: TimelineSubView;
    repairOpen: boolean;
    shareOpen: boolean;
    slugEditorOpen: boolean;
  }

  // Seed the initial history entry on first render so back-navigation
  // has something to return to. replaceState (not pushState) here —
  // we're labelling the entry the user is already on, not adding one.
  useEffect(() => {
    if (historyInitialisedRef.current) return;
    historyInitialisedRef.current = true;
    const payload: CaseHistoryState = {
      kind: "case-view",
      tab,
      timelineSubView,
      repairOpen: repairDatesOpen,
      shareOpen,
      slugEditorOpen,
    };
    window.history.replaceState(payload, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for back/forward and restore the target entry's state.
  useEffect(() => {
    function onPop(e: PopStateEvent) {
      const state = e.state as CaseHistoryState | null;
      if (!state || state.kind !== "case-view") return;
      popstateRestoringRef.current = true;
      // Historical entries may carry the retired "story" tab; remap
      // to the merged Timeline tab with the Written sub-view
      // selected so back-nav still lands the user on the prose.
      if (state.tab === "story") {
        setTab("timeline");
        setTimelineSubViewState("written");
      } else {
        setTab(state.tab);
        if (state.timelineSubView) {
          setTimelineSubViewState(state.timelineSubView);
        }
      }
      setRepairDatesOpen(!!state.repairOpen);
      setShareOpen(!!state.shareOpen);
      setSlugEditorOpen(!!state.slugEditorOpen);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Push a new history entry whenever any tracked state changes —
  // except when the change came from us handling popstate.
  useEffect(() => {
    if (!historyInitialisedRef.current) return;
    if (popstateRestoringRef.current) {
      popstateRestoringRef.current = false;
      return;
    }
    const current = window.history.state as CaseHistoryState | null;
    const next: CaseHistoryState = {
      kind: "case-view",
      tab,
      timelineSubView,
      repairOpen: repairDatesOpen,
      shareOpen,
      slugEditorOpen,
    };
    if (
      current &&
      current.kind === "case-view" &&
      current.tab === next.tab &&
      current.timelineSubView === next.timelineSubView &&
      current.repairOpen === next.repairOpen &&
      current.shareOpen === next.shareOpen &&
      current.slugEditorOpen === next.slugEditorOpen
    ) {
      return; // nothing changed, don't pollute the stack
    }
    window.history.pushState(next, "");
  }, [tab, timelineSubView, repairDatesOpen, shareOpen, slugEditorOpen]);

  // Stable setter for Timeline's `onScrollToRecordHandled` so the
  // scroll-retry effect inside Timeline doesn't get re-run (and
  // reset) every time CaseView re-renders for an unrelated reason.
  const handleScrollToRecordHandled = useCallback(() => {
    setScrollToRecordId(null);
  }, []);

  const handleRecordsDeleted = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const set = new Set(ids);
    setRecords((prev) => prev.filter((r) => !set.has(r.id)));
  }, []);

  const handleRecordUpdated = useCallback((updated: CaseRecord) => {
    setRecords((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  // Records visible in the list with the current routine toggle.
  // The tab label shows this count so the user always knows how many
  // records they're looking at. Routine records are always present
  // in the underlying `records` array and still feed AI / exports.
  const nonRoutineRecordCount = useMemo(
    () => records.filter((r) => r.significance !== "low").length,
    [records],
  );
  const visibleRecordCount = showRoutine
    ? records.length
    : nonRoutineRecordCount;
  const hiddenRoutineCount = records.length - nonRoutineRecordCount;

  // Files-tab badge: unique R2 keys across every record's primary file
  // and attachments. Dedupes so a file present in both places (rare,
  // but possible on legacy rows) counts once.
  const fileCount = useMemo(() => {
    const seen = new Set<string>();
    for (const r of records) {
      if (r.file_r2_key) seen.add(r.file_r2_key);
      for (const att of r.attachments ?? []) seen.add(att.r2_key);
    }
    return seen.size;
  }, [records]);

  async function handleStatusChange(next: CaseStatus) {
    if (!caseData || next === caseData.status) return;
    try {
      const { case: c } = await api.updateCase(caseData.id, { status: next });
      setCaseData(c);
      onCaseUpdated(c);
    } catch (err) {
      window.alert(`Status update failed: ${(err as Error).message}`);
    }
  }

  async function handleDeadlineChange(value: string) {
    if (!caseData) return;
    try {
      const { case: c } = await api.updateCase(caseData.id, {
        deadline: value || null,
      });
      setCaseData(c);
      onCaseUpdated(c);
      setEditingDeadline(false);
    } catch (err) {
      window.alert(`Deadline update failed: ${(err as Error).message}`);
    }
  }

  async function handleGroupChange(nextGroup: string | null) {
    if (!caseData) return;
    const current = caseData.group_name ?? null;
    const next = nextGroup && nextGroup.trim() ? nextGroup.trim() : null;
    if (next === current) return;
    try {
      const { case: c } = await api.updateCase(caseData.id, {
        group_name: next,
      });
      setCaseData(c);
      onCaseUpdated(c);
    } catch (err) {
      window.alert(`Group update failed: ${(err as Error).message}`);
    }
  }

  async function handleDelete() {
    if (!caseData) return;
    if (
      !window.confirm(
        `Delete case "${caseData.name}" and all its records? This cannot be undone.`,
      )
    )
      return;
    try {
      await api.deleteCase(caseData.id);
      onDeleted(caseData.id);
      onCaseChanged();
    } catch (err) {
      // The error banner above only renders when caseData is null
      // (i.e. on the initial load failure), so a delete failure has
      // to surface as a window.alert — otherwise the click looks
      // like a no-op and the case stays put with no explanation.
      const msg = (err as Error).message;
      setError(msg);
      window.alert(`Delete failed: ${msg}`);
    }
  }

  // Expense statement overlay (Round 8) — also reachable from the Overview
  // tab's financial summary.
  const [statementOpen, setStatementOpen] = useState(false);

  async function handleExport(
    format: "pdf" | "docx" | "xlsx" | "zip" | "expenses" | "timeline",
  ) {
    if (!caseData) return;
    setExporting(true);
    try {
      if (format === "zip" || format === "expenses") {
        const blob =
          format === "expenses"
            ? await exportExpenseReportZip(
                { case: caseData, records },
                await getAuthHeaders(),
              )
            : await exportCaseAsZip(
                { case: caseData, records },
                await getAuthHeaders(),
              );
        const suffix = format === "expenses" ? "Expenses" : "Archive";
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `CaseThread - ${caseData.name} - ${suffix} - ${new Date()
          .toISOString()
          .slice(0, 10)}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }
      if (format === "xlsx") {
        await exportCaseAsXlsx({ case: caseData, records, contacts });
        return;
      }
      if (format === "timeline") {
        // Fact-based timeline export — a live read, no AI. Each record
        // becomes a card with its verified facts and verbatim quotes.
        const { timeline } = await api.getTimeline(caseData.id);
        if (!timeline || timeline.length === 0) {
          window.alert(
            "No records to export yet. Add records to the case first.",
          );
          return;
        }
        await exportTimelinePdf({
          caseData,
          records: timeline,
          generatedAt: new Date().toISOString(),
        });
        return;
      }
      // DOCX brief and PDF dossier share a heavy path:
      //   1. Detect staleness (summary / story / key_facts older
      //      than the newest record).
      //   2. Regenerate stale items (each = 1 AI call).
      //   3. Fetch volume summaries for multi-volume cases.
      //   4. Build the bundle and call the renderer.
      //
      // The progress modal walks the user through each phase so
      // they know why the export is taking a few seconds — and if
      // AI calls are being spent they see the running total.
      const steps = [
        "Checking cached data",
        "Preparing case overview",
        "Preparing narrative",
        "Preparing key facts",
        "Preparing volume summaries",
        "Building document",
      ];
      const label =
        format === "pdf"
          ? "Generating Full Dossier..."
          : "Generating Case Brief...";
      let aiCallsUsed = 0;
      setExportProgress({
        label,
        steps,
        currentStep: 0,
        aiCallsUsed,
      });

      const staleness = computeStaleness(caseData, records);
      let updatedCase = caseData;

      // Step 1: case overview — deterministic, fact-based (no AI). Built from
      // verified record_facts, mirroring the Overview tab.
      setExportProgress((prev) => ({
        ...prev!,
        currentStep: 1,
        aiCallInProgress: false,
      }));
      let overview: CaseOverview | null = null;
      // Cover line for the dossier — a fact count, meaningful for a
      // deterministic fact-based dossier (replaces the old AI-summary
      // validation badge, which no longer applies).
      let coverFactLine: string | null = null;
      // Party canonicalizer for the narrative section's party arrays
      // (Round 10, 1D) — same index the overview section gates on.
      let exportCanonicalOf: ((raw: string) => string | null) | undefined;
      try {
        const { facts } = await api.getCaseFacts(caseData.id);
        // Participation evidence for the parties gate; exports degrade to
        // local body tails if the fetch fails.
        const evidence = await api
          .getParticipationEvidence(caseData.id)
          .catch(() => undefined);
        const exportPartyIndex = buildPartyIndex(facts, records, evidence);
        exportCanonicalOf = (raw) => exportPartyIndex.canonicalOf(raw);
        overview = buildCaseOverview(updatedCase, records, facts, evidence);
        coverFactLine =
          facts.length === 0
            ? "Facts have not been extracted for this case"
            : `${facts.length} verified fact${facts.length === 1 ? "" : "s"} from ${records.length} record${records.length === 1 ? "" : "s"}`;
      } catch (err) {
        // The overview gracefully degrades to a placeholder if the fetch
        // fails — the rest of the export still completes.
        console.warn("[export] facts fetch failed", err);
      }

      // Step 2: chronological narrative (story)
      // Step 2: chronological narrative. After the single-source-
      // timeline consolidation, the narrative is no longer a
      // separate AI call on `cases.story` — it is rendered
      // deterministically from the cached timeline events. Fetch
      // those once; the PDF/DOCX renderer will format them.
      //
      // If the timeline is stale (or missing) we auto-regenerate it
      // here — one AI call covers both the Visual and Written
      // sections of the dossier.
      setExportProgress((prev) => ({
        ...prev!,
        currentStep: 2,
        aiCallInProgress: false,
      }));
      // Narrative is rendered deterministically from the live fact-based
      // timeline (records + verified facts). No AI call.
      let cachedTimeline: TimelineEvent[] | null = null;
      try {
        const r = await api.getTimeline(caseData.id);
        cachedTimeline = timelineRecordsToEvents(r.timeline, exportCanonicalOf);
      } catch (err) {
        // Narrative section gracefully degrades to a placeholder if the
        // fetch fails — the rest of the export still completes.
        console.warn("[export] timeline fetch failed", err);
      }
      setExportProgress((prev) => ({
        ...prev!,
        aiCallsUsed,
        aiCallInProgress: false,
      }));

      // Step 3: key facts
      setExportProgress((prev) => ({
        ...prev!,
        currentStep: 3,
        aiCallInProgress: staleness.keyFacts,
      }));
      if (staleness.keyFacts) {
        const r = await api.refreshKeyFacts(caseData.id);
        aiCallsUsed += 1;
        updatedCase = {
          ...updatedCase,
          // key_facts column stores the JSON-stringified array.
          key_facts: JSON.stringify(r.key_facts),
          key_facts_updated_at:
            r.updated_at ?? updatedCase.key_facts_updated_at,
        };
        setExportProgress((prev) => ({
          ...prev!,
          aiCallsUsed,
          aiCallInProgress: false,
        }));
      }

      // Step 4: volume summaries (multi-volume cases only). Always
      // refetch the current volume list — the sidebar's copy may lag
      // during active work — and pull closed-volume briefs in parallel.
      setExportProgress((prev) => ({
        ...prev!,
        currentStep: 4,
        aiCallInProgress: false,
      }));
      const { volumes: freshVolumes } = await api.listVolumes(caseData.id);
      const closed = freshVolumes.filter((v) => v.closed_at !== null);
      const briefs: VolumeBriefEntry[] = await Promise.all(
        closed.map(async (v) => {
          try {
            const r = await api.volumeSummary(caseData.id, v.id);
            return {
              volume: r.volume,
              summary: r.summary,
              keyFacts: r.key_facts,
            };
          } catch {
            return { volume: v, summary: null, keyFacts: [] };
          }
        }),
      );

      // Step 5: build the document. PDF needs auth headers so the
      // Appendix B figure fetches can pull R2 blobs.
      setExportProgress((prev) => ({ ...prev!, currentStep: 5 }));
      // Render the chronological narrative deterministically from
      // the cached timeline events. No AI call. Null narrative means
      // "timeline not generated yet" — renderers will show the
      // placeholder copy.
      const narrative: string | null =
        cachedTimeline && cachedTimeline.length > 0
          ? renderProseFromEvents({
              events: cachedTimeline,
              caseData: updatedCase,
              statusParagraph: buildStatusParagraph(
                updatedCase,
                records,
                briefs,
              ),
            })
          : null;
      // Cost scan (best-effort) and a cover validation badge — PDF only.
      let costs = null;
      if (format === "pdf") {
        try {
          const cr = await api.getCosts(caseData.id);
          costs = cr.costs ?? null;
        } catch {
          /* no cost scan yet — section is simply omitted */
        }
      }
      const bundle = {
        case: updatedCase,
        records,
        contacts,
        volumes: freshVolumes,
        volumeBriefs: briefs,
        aiCallsUsed,
        narrative,
        overview,
        costs,
        coverFactLine,
      };
      if (format === "pdf") {
        const authHeaders = await getAuthHeaders();
        const stats = await exportCaseDossierPdf(
          bundle,
          authHeaders,
          (label) => {
            // Reflect per-record progress in the final "Building
            // document" step so the user sees "section N of M".
            setExportProgress((prev) =>
              prev
                ? {
                    ...prev,
                    steps: prev.steps.map((s, i) =>
                      i === prev.steps.length - 1
                        ? `Building document — ${label}`
                        : s,
                    ),
                  }
                : prev,
            );
          },
        );
        setExportProgress(null);
        window.alert(
          `Dossier generated: ${stats.pages} page${stats.pages === 1 ? "" : "s"}, ${stats.attachmentsIncluded} attachment${stats.attachmentsIncluded === 1 ? "" : "s"} included, ${stats.attachmentsFailed} failed.`,
        );
      } else {
        await exportCaseAsDocx(bundle);
      }

      // Persist the refreshed caseData locally so the user doesn't
      // see the stale version after export completes.
      setCaseData(updatedCase);
      setExportProgress(null);
    } catch (err) {
      const message = (err as Error).message;
      // If a progress modal is open, surface the error in it so the
      // user sees which step failed + the running AI-call total.
      // Otherwise (zip/xlsx/timeline paths) fall back to a plain
      // alert — those finish fast enough that the modal overhead
      // isn't worth it.
      setExportProgress((prev) =>
        prev ? { ...prev, error: message } : null,
      );
      if (!exportProgress) {
        window.alert(`Export failed: ${message}`);
      }
    } finally {
      setExporting(false);
    }
  }

  async function handleRecomputeVolumeStats() {
    if (!caseData) return;
    setBulkBusy("volstats");
    setBulkMessage(null);
    try {
      const r = await api.recomputeVolumeStats(caseData.id);
      setVolumes(r.volumes);
      setBulkMessage(
        `Recomputed stats for ${r.recomputed} volume${r.recomputed === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      setBulkMessage(`Recompute failed: ${(err as Error).message}`);
    } finally {
      setBulkBusy(null);
    }
  }

  async function handleRethread() {
    setBulkBusy("thread");
    setBulkMessage(null);
    try {
      const r = await api.rethread(caseData!.id);
      const baseMsg =
        r.assignments === 0
          ? "No new threads found."
          : `Grouped ${r.assignments} record${r.assignments === 1 ? "" : "s"} into ${r.threads} thread${r.threads === 1 ? "" : "s"}.`;
      const orphanMsg =
        r.orphans_cleared > 0
          ? ` Cleared ${r.orphans_cleared} orphan thread assignment${r.orphans_cleared === 1 ? "" : "s"}.`
          : "";
      setBulkMessage(baseMsg + orphanMsg);
      await refresh();
    } catch (err) {
      setBulkMessage(`Rethread failed: ${(err as Error).message}`);
    } finally {
      setBulkBusy(null);
    }
  }

  async function handleRunAudit() {
    if (!caseData) return;
    setBulkBusy("audit");
    setBulkMessage(null);
    try {
      const r = await api.runAudit(caseData.id);
      setBulkMessage(
        `Audited ${r.audited} record${r.audited === 1 ? "" : "s"}: ${r.verified} verified, ${r.flagged} flagged.`,
      );
      await refresh();
    } catch (err) {
      setBulkMessage(`Audit failed: ${(err as Error).message}`);
    } finally {
      setBulkBusy(null);
    }
  }

  async function handleExtractAllFacts() {
    if (!caseData) return;
    setBulkBusy("facts");
    // Show progress immediately — the first chunk takes ~30s, so without this
    // the banner wouldn't appear until the first response lands. `records` is
    // already loaded, so we can show the real total up front.
    setBulkMessage(`Extracting facts… record 0 of ${records.length}`);
    try {
      const r = await api.extractAllFacts(caseData.id, (p) =>
        setBulkMessage(`Extracting facts… record ${p.completed} of ${p.total}`),
      );
      const dropNotice = describeDropFlags(r.flagged);
      setBulkMessage(
        r.partial
          ? `Extraction incomplete — ${r.stored} fact${r.stored === 1 ? "" : "s"} stored, then stopped at a problem chunk. Run "Extract all facts" again to finish.`
          : `Extracted ${r.stored} verified fact${r.stored === 1 ? "" : "s"} across ${r.total} record${r.total === 1 ? "" : "s"}.${dropNotice ? ` ${dropNotice}` : ""}`,
      );
      setFactsNonce((n) => n + 1);
    } catch (err) {
      setBulkMessage(`Fact extraction failed: ${(err as Error).message}`);
    } finally {
      setBulkBusy(null);
    }
  }

  async function handleUnthread() {
    if (!caseData) return;
    const ok = window.confirm(
      "This will ungroup all threaded records. They will appear as individual records again. Continue?",
    );
    if (!ok) return;
    setBulkBusy("unthread");
    setBulkMessage(null);
    try {
      const r = await api.unthread(caseData.id);
      setBulkMessage(
        r.cleared === 0
          ? "No threaded records to ungroup."
          : `Ungrouped ${r.cleared} record${r.cleared === 1 ? "" : "s"} — they now appear as individuals.`,
      );
      await refresh();
    } catch (err) {
      setBulkMessage(`Ungroup failed: ${(err as Error).message}`);
    } finally {
      setBulkBusy(null);
    }
  }

  // Single public email address for this case — always the readable
  // email_slug. The legacy forward_address hash still routes to the
  // same case via case_email_aliases on the server, but we don't
  // surface it anywhere in the UI or exports: users only ever see
  // one address. Rows that haven't been slug-backfilled yet fall
  // back to forward_address so the header never renders empty.
  const primaryEmail = caseData
    ? caseData.email_slug
      ? `${caseData.email_slug}@${caseData.forward_address.split("@")[1] ?? "casethread.app"}`
      : caseData.forward_address
    : "";

  function downloadVCard() {
    if (!caseData) return;
    // Escape vCard-unsafe characters (commas, semicolons, backslashes,
    // newlines) per RFC 6350.
    const esc = (s: string) =>
      s.replace(/[\\,;\n]/g, (c) => {
        if (c === "\n") return "\\n";
        return `\\${c}`;
      });
    const fn = `CaseThread — ${caseData.name}`;
    const lines = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${esc(fn)}`,
      `EMAIL;TYPE=WORK:${primaryEmail}`,
      `NOTE:${esc(
        `CaseThread forwarding address for ${caseData.name}. Forward or CC correspondence to this address to file it automatically.`,
      )}`,
      "END:VCARD",
    ];
    const content = lines.join("\r\n") + "\r\n";
    const blob = new Blob([content], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = caseData.name.replace(/[\\/:*?"<>|]/g, "-").slice(0, 80);
    a.href = url;
    a.download = `CaseThread - ${safeName}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function copyPrimaryEmail() {
    if (!primaryEmail) return;
    try {
      await navigator.clipboard.writeText(primaryEmail);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* user can select manually */
    }
  }

  function openAddMenu() {
    setTab("records");
    setAddMenuOpen(true);
  }
  function pickAddMode(mode: AddMode) {
    setAddMenuOpen(false);
    setAddMode(mode);
  }

  // Response timers: most recent outbound / inbound records by direction.
  const { lastSent, lastReceived } = useMemo(() => {
    let sent: CaseRecord | null = null;
    let received: CaseRecord | null = null;
    for (const r of records) {
      if (r.source_type !== "email") continue;
      if (r.direction === "outbound") {
        if (!sent || r.record_date > sent.record_date) sent = r;
      } else if (r.direction === "inbound") {
        if (!received || r.record_date > received.record_date) received = r;
      }
    }
    return { lastSent: sent, lastReceived: received };
  }, [records]);

  if (loading && !caseData) return <div className="loading">Loading…</div>;
  if (error && !caseData)
    return <div className="banner-error">Error: {error}</div>;
  if (!caseData) return null;

  const deadlineKind = deadlineVariant(caseData.deadline);
  const dlClass =
    deadlineKind === "past"
      ? "deadline-past"
      : deadlineKind === "near"
        ? "deadline-near"
        : "deadline-future";

  const receivedInfo = lastReceived
    ? relativeDays(lastReceived.record_date)
    : null;
  const receivedUrgent =
    receivedInfo && receivedInfo.days >= 14
      ? "urgent"
      : receivedInfo && receivedInfo.days >= 7
        ? "warn"
        : "";

  // Data-quality roll-up for the case header. A record counts as
  // "needs review" when its persisted extraction_confidence.date is
  // "fallback" — meaning the extractor found no date in the content
  // and the filing time was used. Records whose confidence blob is
  // missing entirely (pre-0035) are treated as "verified" to avoid
  // scaring users about rows they can't actually fix; the repair-
  // dates modal remains the bulk-cleanup entry point for those.
  const dataQuality = (() => {
    let needsReview = 0;
    let verified = 0;
    for (const r of records) {
      const conf = parseExtractionConfidence(r.extraction_confidence);
      if (conf && conf.date === "fallback") needsReview += 1;
      else verified += 1;
    }
    return { needsReview, verified };
  })();

  // Unaddressed QA/export-gate findings across the case's stored validation
  // results (summary + costs) — surfaced by the Overview's needs-attention
  // block and reviewable in the QA findings panel (Round 10.1). Held in
  // state so dismiss/accept in the panel updates the count immediately.
  const qaFlagCount = qaUnaddressedCount(qaValidations);

  return (
    <div className="case-view">
      <header className="case-header">
        <div className="case-header-main">
          <div className="case-title-row">
            <h1>{caseData.name}</h1>
            <StatusBadge
              status={caseData.status}
              onChange={handleStatusChange}
            />
            <GroupPicker
              currentGroup={caseData.group_name ?? null}
              onChange={handleGroupChange}
            />
          </div>
          {caseData.description && (
            <p className="muted">{caseData.description}</p>
          )}

          <div className="case-stats">
            {caseData.deadline && (
              <span className={`case-deadline ${dlClass}`}>
                {deadlineKind === "past"
                  ? "Deadline passed"
                  : deadlineKind === "near"
                    ? "Deadline soon"
                    : "Response due"}{" "}
                <strong>{formatLongDate(caseData.deadline)}</strong>
                <Tooltip title="Edit response deadline">
                  <button
                    type="button"
                    className="case-deadline-edit"
                    onClick={() => setEditingDeadline((v) => !v)}
                    aria-label="Edit response deadline"
                  >
                    ✎
                  </button>
                </Tooltip>
              </span>
            )}
            {!caseData.deadline && (
              <Tooltip title="Set a response deadline with email reminder">
                <button
                  type="button"
                  className="case-deadline-add"
                  onClick={() => setEditingDeadline((v) => !v)}
                >
                  + Set deadline
                </button>
              </Tooltip>
            )}
            {editingDeadline && (
              <DeadlinePicker
                value={caseData.deadline}
                onSave={handleDeadlineChange}
                onClear={() => handleDeadlineChange("")}
                onCancel={() => setEditingDeadline(false)}
              />
            )}

            {lastSent && (
              <span className="stat-badge stat-sent">
                Last sent:{" "}
                <strong>{relativeDays(lastSent.record_date).label}</strong>
              </span>
            )}
            {receivedInfo && (
              <span
                className={`stat-badge stat-received ${receivedUrgent}`}
                title={
                  receivedUrgent
                    ? `No reply in ${receivedInfo.days} days`
                    : undefined
                }
              >
                Last received: <strong>{receivedInfo.label}</strong>
              </span>
            )}
            {records.length > 0 && (
              <span
                className="stat-badge stat-quality"
                title="Per-field extraction confidence. Records with no date in content are flagged 'needs review'."
              >
                {records.length} record
                {records.length === 1 ? "" : "s"} ·{" "}
                <strong>{dataQuality.verified}</strong> dates
                verified
                {dataQuality.needsReview > 0 && (
                  <>
                    {" "}
                    ·{" "}
                    <button
                      type="button"
                      className="stat-quality-review"
                      onClick={() => {
                        setTab("records");
                        setOnlyFallbackDates(true);
                      }}
                    >
                      {dataQuality.needsReview} need review
                    </button>
                  </>
                )}
              </span>
            )}
          </div>

          <div className="forward-address-compact">
            <span aria-hidden>📋</span>
            <code className="forward-address-inline">{primaryEmail}</code>
            <Tooltip title="Copy forwarding address to clipboard">
              <button
                type="button"
                className="forward-address-copy"
                onClick={copyPrimaryEmail}
                aria-label="Copy forwarding address to clipboard"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </Tooltip>
            <Tooltip title="Download a contact card for your email app">
              <button
                type="button"
                className="forward-address-copy"
                onClick={downloadVCard}
                aria-label="Download a contact card for your email app"
              >
                📇 Add to contacts
              </button>
            </Tooltip>
            <Tooltip title="Change your forwarding address">
              <button
                type="button"
                className="forward-address-copy"
                onClick={() => setSlugEditorOpen(true)}
                aria-label="Change your forwarding address"
              >
                Edit
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="case-header-actions">
          <Tooltip title="Generate a read-only link to share this case with an advisor">
            <Button
              variant="primary"
              size="md"
              onClick={() => setShareOpen(true)}
            >
              Share
            </Button>
          </Tooltip>
          <Tooltip title="Download the case as a brief, dossier, spreadsheet, or archive">
            <Dropdown
              trigger={<>{exporting ? "Exporting…" : "Export"}</>}
              variant="primary"
              size="md"
              disabled={exporting}
              items={[
                {
                  label: `Case brief (Word) — ${records.length} record${records.length === 1 ? "" : "s"}`,
                  leading: "📝",
                  disabled: exporting,
                  onSelect: () => void handleExport("docx"),
                },
                {
                  label: `Full dossier (PDF) — ${records.length} record${records.length === 1 ? "" : "s"}`,
                  leading: "📄",
                  disabled: exporting,
                  onSelect: () => void handleExport("pdf"),
                },
                {
                  label: "Timeline infographic (PDF)",
                  leading: "🗓",
                  disabled: exporting,
                  onSelect: () => void handleExport("timeline"),
                },
                {
                  label: "Expense statement (curate & export)",
                  leading: "🧾",
                  disabled: exporting,
                  onSelect: () => setStatementOpen(true),
                },
                {
                  label: "Spreadsheet (Excel)",
                  leading: "📊",
                  disabled: exporting,
                  onSelect: () => void handleExport("xlsx"),
                },
                {
                  label: "Archive (ZIP)",
                  leading: "🗜",
                  disabled: exporting,
                  onSelect: () => void handleExport("zip"),
                },
                // Expense report option appears only when the case has
                // something to expense — either a record carries an
                // extracted amount from intake or an image attachment
                // we can package as a receipt.
                ...(hasExpenseData(records)
                  ? [
                      {
                        label: "Expense report (ZIP)",
                        leading: "🧾",
                        disabled: exporting,
                        onSelect: () => void handleExport("expenses"),
                      },
                    ]
                  : []),
              ]}
            />
          </Tooltip>
          {!isMobile && (
            <>
              <Tooltip title="Case maintenance actions">
                <Dropdown
                  trigger={
                    <>
                      <span aria-hidden>⚙</span> Maintain
                    </>
                  }
                  variant="secondary"
                  size="md"
                  items={[
                    {
                      label:
                        bulkBusy === "thread"
                          ? "Grouping…"
                          : "Group into threads",
                      leading: "🧵",
                      disabled: bulkBusy !== null,
                      onSelect: handleRethread,
                    },
                    {
                      label:
                        bulkBusy === "unthread"
                          ? "Ungrouping…"
                          : "Ungroup threads",
                      leading: "🧶",
                      disabled: bulkBusy !== null,
                      onSelect: handleUnthread,
                    },
                    {
                      label: "Repair dates",
                      leading: "📅",
                      onSelect: () => setRepairDatesOpen(true),
                    },
                    {
                      label:
                        bulkBusy === "audit"
                          ? "Auditing…"
                          : "Run extraction audit",
                      leading: "🔎",
                      disabled: bulkBusy !== null,
                      onSelect: handleRunAudit,
                    },
                    {
                      label:
                        bulkBusy === "facts"
                          ? "Extracting facts…"
                          : "Extract all facts",
                      leading: "🗂",
                      disabled: bulkBusy !== null,
                      onSelect: handleExtractAllFacts,
                    },
                    {
                      label:
                        bulkBusy === "volstats"
                          ? "Recomputing…"
                          : "Recompute volume stats",
                      leading: "📊",
                      disabled: bulkBusy !== null,
                      onSelect: handleRecomputeVolumeStats,
                    },
                  ]}
                />
              </Tooltip>
              <Tooltip title="Permanently delete this case and all its records">
                <button
                  type="button"
                  className="case-delete-link"
                  onClick={handleDelete}
                >
                  Delete case
                </button>
              </Tooltip>
            </>
          )}
          {isMobile && (
            <Dropdown
              trigger={<span aria-hidden>⋯</span>}
              variant="secondary"
              size="md"
              hideCaret
              ariaLabel="More actions"
              items={[
                {
                  label:
                    bulkBusy === "thread"
                      ? "Grouping…"
                      : "Group into threads",
                  leading: "🧵",
                  disabled: bulkBusy !== null,
                  onSelect: handleRethread,
                },
                {
                  label:
                    bulkBusy === "unthread"
                      ? "Ungrouping…"
                      : "Ungroup threads",
                  leading: "🧶",
                  disabled: bulkBusy !== null,
                  onSelect: handleUnthread,
                },
                {
                  label: "Repair dates",
                  leading: "📅",
                  onSelect: () => setRepairDatesOpen(true),
                },
                {
                  label: "Delete case",
                  leading: "🗑",
                  tone: "danger",
                  onSelect: handleDelete,
                },
              ]}
            />
          )}
        </div>
      </header>
      {bulkMessage && (
        <div className="bulk-toast" role="status">
          <span>{bulkMessage}</span>
          <button type="button" onClick={() => setBulkMessage(null)}>
            Dismiss
          </button>
        </div>
      )}
      {autoScoreStatus && autoScoreStatus !== "done" && (
        <div className="auto-score-toast" role="status" aria-live="polite">
          <span className="auto-score-spinner" aria-hidden />
          <span>
            {autoScoreStatus === "scoring"
              ? "Scoring new records…"
              : "Generating key facts…"}
          </span>
        </div>
      )}
      {autoScoreStatus === "done" && (
        <div className="auto-score-toast auto-score-done" role="status">
          <span aria-hidden>✓</span>
          <span>Case ready — records scored.</span>
        </div>
      )}
      {recentlyHiddenCount !== null && recentlyHiddenCount > 0 && (
        <div
          className="auto-score-toast routine-hidden-toast"
          role="status"
          aria-live="polite"
        >
          <span aria-hidden>👁</span>
          <span>
            {recentlyHiddenCount === 1
              ? "1 record was classified as routine and hidden."
              : `${recentlyHiddenCount} records were classified as routine and hidden.`}
          </span>
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setShowRoutine(true);
              setRecentlyHiddenCount(null);
            }}
          >
            Show
          </button>
          <button
            type="button"
            className="link-button"
            onClick={() => setRecentlyHiddenCount(null)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <VolumeBanner
        caseId={caseData.id}
        volumes={volumes}
        onVolumeChanged={() => {
          void refreshVolumes();
          void refresh();
        }}
      />


      <nav className="tabs" aria-label="Case sections">
        <button
          className={`tab-nav${tab === "overview" ? " active" : ""}`}
          onClick={() => setTab("overview")}
        >
          Overview
        </button>
        <button
          className={`tab-nav${tab === "records" ? " active" : ""}`}
          onClick={() => {
            // A manual Records visit is never "coming from a source view" —
            // clear any stale pending focus/return so neither back pill
            // (Financials or the shared cross-tab BackBar) appears (audit L3).
            setSummaryFocusRow(null);
            setRecordReturn(null);
            setTab("records");
          }}
        >
          Records (
          {!showRoutine && hiddenRoutineCount > 0
            ? `${visibleRecordCount} shown · ${hiddenRoutineCount} hidden`
            : visibleRecordCount}
          )
        </button>
        <button
          className={`tab-nav${tab === "timeline" ? " active" : ""}`}
          onClick={() => setTab("timeline")}
        >
          Timeline
        </button>
        <button
          className={`tab-nav${tab === "summary" ? " active" : ""}`}
          onClick={() => {
            // Entering Financials by normal nav must not auto-scroll/flash a
            // stale row left over from an abandoned earlier jump (audit L3).
            setSummaryFocusRow(null);
            setTab("summary");
          }}
        >
          Financials
        </button>
        <button
          className={`tab-nav${tab === "files" ? " active" : ""}`}
          onClick={() => setTab("files")}
        >
          Files ({fileCount})
        </button>
        <button
          className={`tab-nav${tab === "contacts" ? " active" : ""}`}
          onClick={() => setTab("contacts")}
        >
          Contacts ({contacts.length})
        </button>
        <span className="tabs-spacer" aria-hidden />
        <button className="tab-add" onClick={openAddMenu}>
          + Add
        </button>
        <button
          className={`tab-interrogate${tab === "interrogate" ? " active" : ""}`}
          onClick={() => setTab("interrogate")}
        >
          Interrogate
        </button>
      </nav>

      <section className="tab-panel">
        {tab === "overview" && (
          <>
            <StructuredOverview
              caseId={caseData.id}
              caseName={caseData.name}
              records={records}
              onNavigateToRecord={(id) =>
                navigateToRecord(id, null, { tab: "overview", label: "Overview" })
              }
              refreshNonce={factsNonce}
              onShowNeedsReview={() => {
                setTab("records");
                setOnlyFallbackDates(true);
              }}
              qaFlagCount={qaFlagCount}
              onShowQaFlags={() => setQaPanelOpen(true)}
            />

            {qaPanelOpen && (
              <QaFindingsPanel
                sections={qaValidations}
                onAction={onQaAction}
                onClose={() => setQaPanelOpen(false)}
              />
            )}

            <KeyFactsPanel
              caseId={caseData.id}
              records={records}
              onNavigateToRecord={(id) =>
                navigateToRecord(id, null, { tab: "overview", label: "Overview" })
              }
            />
            <div id="ov-costs-panel">
              <CostsPanel caseId={caseData.id} />
            </div>
            <LinkedCases
              caseId={caseData.id}
              allCases={allCases.filter((x) => x.id !== caseData.id)}
              links={links}
              onChanged={setLinks}
              onNavigateCase={onNavigateCase}
            />
          </>
        )}
        {tab === "records" && shouldShowBackToSummary(tab, summaryFocusRow) && (
          <BackToSummaryBar onBack={() => setTab("summary")} />
        )}
        {shouldShowRecordBack(tab, recordReturn, summaryFocusRow) && (
          <BackBar
            label={recordBackLabel(recordReturn!)}
            hint="Back returns to where you were"
            onBack={() => {
              const dest = recordReturn!.tab as Tab;
              setRecordReturn(null);
              setTab(dest);
            }}
            variant="floating"
          />
        )}
        {tab === "records" && (
          <Timeline
            caseId={caseData.id}
            records={records}
            volumes={volumes}
            showSummaryVolumeId={showSummaryVolumeId}
            onShowSummaryHandled={onShowSummaryHandled}
            onDeleted={handleRecordsDeleted}
            onRecordCreated={handleRecordCreated}
            onRecordUpdated={handleRecordUpdated}
            onRefresh={refresh}
            onAskAiAboutVolume={(volumeId) => {
              setInterrogateScope(volumeId);
              setTab("interrogate");
            }}
            scrollToRecordId={scrollToRecordId}
            onScrollToRecordHandled={handleScrollToRecordHandled}
            onlyFallbackDates={onlyFallbackDates}
            onFallbackFilterHandled={() => setOnlyFallbackDates(false)}
            showRoutine={showRoutine}
            onShowRoutineChange={setShowRoutine}
            hiddenRoutineCount={hiddenRoutineCount}
          />
        )}
        {tab === "timeline" && (
          <TimelineTab
            caseData={caseData}
            records={records}
            onNavigateToRecord={(id) =>
              navigateToRecord(id, null, { tab: "timeline", label: "Timeline" })
            }
            subView={timelineSubView}
            onSubViewChange={setTimelineSubView}
          />
        )}
        {tab === "summary" && (
          <ReimbursementSummaryView
            caseId={caseData.id}
            caseName={caseData.name}
            records={records}
            contacts={contacts}
            onContactsChanged={setContacts}
            refreshNonce={factsNonce}
            onNavigateToRecord={(recordId, attachmentId, rowKey) => {
              if (rowKey) {
                setSummaryFocusRow((prev) => ({
                  key: rowKey,
                  n: (prev?.n ?? 0) + 1,
                }));
              }
              navigateToRecord(recordId, attachmentId);
            }}
            focusRow={summaryFocusRow}
            onFocusRowHandled={() => setSummaryFocusRow(null)}
          />
        )}
        {tab === "files" && (
          <FilesTab
            caseId={caseData.id}
            records={records}
            onNavigateToRecord={(id) =>
              navigateToRecord(id, null, { tab: "files", label: "Files" })
            }
            onRecordUpdated={handleRecordUpdated}
            onRefresh={refresh}
          />
        )}
        {tab === "contacts" && (
          <ContactsTab
            caseId={caseData.id}
            contacts={contacts}
            onContactsChanged={setContacts}
          />
        )}
        {tab === "interrogate" && (
          <Interrogate
            // Remount on case switch so in-flight chat history from
            // the previous case doesn't bleed into the new one. Scope
            // defaults to "case" everywhere, so the scope-change
            // effect below alone isn't enough to clear on navigation.
            key={caseData.id}
            caseId={caseData.id}
            recordCount={records.length}
            volumes={volumes}
            scope={interrogateScope}
            onScopeChange={setInterrogateScope}
            initialMessage={pendingInterrogatePrompt}
            onInitialMessageConsumed={() =>
              setPendingInterrogatePrompt(null)
            }
            records={records}
            // Only the drawer's deep "Open full record ↗" reaches this prop
            // (list + citations open the in-place source drawer). It unmounts
            // Interrogate, so offer the way back to the Interrogate tab.
            onNavigateToRecord={(id) =>
              navigateToRecord(id, null, {
                tab: "interrogate",
                label: "Interrogate",
              })
            }
          />
        )}
      </section>

      <AddMenu
        open={addMenuOpen}
        onClose={() => setAddMenuOpen(false)}
        onSelect={pickAddMode}
      />

      {statementOpen && (
        <ExpenseStatement
          caseId={caseData.id}
          caseName={caseData.name}
          records={records}
          onClose={() => setStatementOpen(false)}
          onNavigateToRecord={navigateToRecord}
        />
      )}

      {addMode && addMode !== "write" && (
        <CaptureOverlay
          title={OVERLAY_TITLES[addMode]}
          onClose={() => setAddMode(null)}
        >
          {addMode === "paste" && (
            <PasteCanvas
              caseId={caseData.id}
              onRecordCreated={handleRecordCreated}
            />
          )}
          {addMode === "receipt" && (
            <PasteCanvas
              caseId={caseData.id}
              onRecordCreated={handleRecordCreated}
              mode="receipt"
            />
          )}
          {addMode === "record" && (
            <RecordCall
              caseId={caseData.id}
              onRecordCreated={handleRecordCreated}
            />
          )}
          {addMode === "file" && (
            <FileUploadPanel
              caseId={caseData.id}
              mode="file"
              onRecordCreated={handleRecordCreated}
              onDone={() => setAddMode(null)}
            />
          )}
          {addMode === "audio" && (
            <FileUploadPanel
              caseId={caseData.id}
              mode="audio"
              onRecordCreated={handleRecordCreated}
              onDone={() => setAddMode(null)}
            />
          )}
        </CaptureOverlay>
      )}

      {addMode === "write" && (
        <WriteRecordModal
          caseId={caseData.id}
          existingRecords={records}
          onClose={() => setAddMode(null)}
          onRecordCreated={(r) => {
            handleRecordCreated(r);
            setAddMode(null);
            setBulkMessage("Record created.");
            // Fire-and-forget significance + key-fact scoring so the
            // new manual record matches the AI treatment uploaded
            // records get. scoreAll is idempotent — already-scored
            // records are skipped server-side.
            void api.scoreAll(caseData.id).catch(() => {
              /* non-fatal */
            });
          }}
        />
      )}

      {shareOpen && (
        <ShareManager
          caseId={caseData.id}
          onClose={() => setShareOpen(false)}
        />
      )}

      <ExportProgressModal
        state={exportProgress}
        onClose={() => setExportProgress(null)}
      />

      {repairDatesOpen && (
        <RepairDatesModal
          caseId={caseData.id}
          onClose={() => {
            setRepairDatesOpen(false);
            // Refresh only on explicit close so the modal doesn't
            // re-render (and lose scroll position) after every
            // single-row Apply. The running count the modal tracks
            // internally is authoritative for the toast message.
            if (repairAppliedTotalRef.current > 0) {
              const n = repairAppliedTotalRef.current;
              setBulkMessage(
                `Updated ${n} record${n === 1 ? "" : "s"}. Regenerate the timeline to see the new dates.`,
              );
              void refresh();
              repairAppliedTotalRef.current = 0;
            }
          }}
          onApplied={(count) => {
            // Modal stays open. We accumulate here so the on-close
            // toast can report the total without the modal having
            // to know about the parent's state.
            repairAppliedTotalRef.current += count;
          }}
          onViewRecord={(recordId) => {
            // "View record ↗" is the one path that DOES close the
            // modal — the user is leaving the repair flow for the
            // Records tab. Running-applied count still fires the
            // refresh + toast via the onClose handler above.
            setRepairDatesOpen(false);
            if (repairAppliedTotalRef.current > 0) {
              const n = repairAppliedTotalRef.current;
              setBulkMessage(
                `Updated ${n} record${n === 1 ? "" : "s"}. Regenerate the timeline to see the new dates.`,
              );
              void refresh();
              repairAppliedTotalRef.current = 0;
            }
            navigateToRecord(recordId);
          }}
        />
      )}

      {slugEditorOpen && (
        <SlugEditor
          caseData={caseData}
          onClose={() => setSlugEditorOpen(false)}
          onSaved={(next) => {
            setCaseData(next);
            onCaseUpdated(next);
            setSlugEditorOpen(false);
          }}
        />
      )}
    </div>
  );
}

function SlugEditor({
  caseData,
  onClose,
  onSaved,
}: {
  caseData: Case;
  onClose: () => void;
  onSaved: (next: Case) => void;
}) {
  // Edits the readable email_slug (e.g. "uv"), not the legacy
  // forward_address hash. When the user changes this, the old slug
  // becomes a permanent alias server-side, so in-flight mail to the
  // previous address keeps arriving.
  const domain =
    caseData.forward_address.split("@")[1] ?? "casethread.app";
  const currentSlug =
    caseData.email_slug ?? (caseData.forward_address.split("@")[0] ?? "");
  const [slug, setSlug] = useState(currentSlug);
  const [check, setCheck] = useState<{
    status: "idle" | "checking" | "ok" | "taken" | "invalid";
    message: string;
  }>({ status: "idle", message: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  useModalEscape(onClose);

  function normalise(raw: string): string {
    return raw
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "")
      .replace(/-{2,}/g, "-")
      .slice(0, 40);
  }

  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (!slug || slug === currentSlug) {
      setCheck({ status: "idle", message: "" });
      return;
    }
    if (slug.length < 2) {
      setCheck({ status: "invalid", message: "Must be at least 2 characters" });
      return;
    }
    setCheck({ status: "checking", message: "Checking…" });
    debounceRef.current = window.setTimeout(async () => {
      try {
        const r = await api.checkEmailSlug(slug);
        if (r.available) {
          setCheck({
            status: "ok",
            message: `✓ ${r.full_address} is available`,
          });
        } else {
          setCheck({
            status: r.error === "slug_reserved" ? "invalid" : "taken",
            message:
              r.error === "slug_reserved"
                ? "✗ This slug is reserved — pick another"
                : r.error === "slug_too_short"
                  ? "✗ Too short — use at least 2 characters"
                  : r.error === "slug_too_long"
                    ? "✗ Too long — 40 characters max"
                    : r.error === "slug_invalid"
                      ? "✗ Use only letters, numbers and hyphens"
                      : "✗ Already taken",
          });
        }
      } catch {
        setCheck({ status: "idle", message: "" });
      }
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [slug, currentSlug]);

  async function save() {
    if (!slug || slug === currentSlug) return;
    setSaving(true);
    setError(null);
    try {
      const { case: next } = await api.updateCaseEmailSlug(
        caseData.id,
        slug,
      );
      onSaved(next);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="modal-panel slug-editor-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>Change forwarding address</h3>
          <button type="button" onClick={onClose} className="btn-subtle">
            Close
          </button>
        </div>
        <p className="muted">
          Pick a new readable address for this case. The old address
          keeps working — in-flight mail to it still lands here, so
          you don't need to update saved contacts right away.
        </p>
        <div className="slug-input-row">
          <input
            type="text"
            className="slug-input"
            value={slug}
            onChange={(e) => setSlug(normalise(e.target.value))}
            placeholder="new-slug"
            maxLength={40}
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
          <span className="slug-suffix">@{domain}</span>
        </div>
        <small
          className={`slug-status slug-status-${check.status}`}
          aria-live="polite"
        >
          {check.message || (
            <>
              Current: <code>{currentSlug}@{domain}</code>
            </>
          )}
        </small>
        {error && <div className="form-error">{error}</div>}
        <div className="form-actions">
          <button type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={save}
            disabled={
              saving ||
              !slug ||
              slug === currentSlug ||
              check.status === "taken" ||
              check.status === "invalid" ||
              check.status === "checking"
            }
          >
            {saving ? "Saving…" : "Change address"}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function StatusBadge({
  status,
  onChange,
}: {
  status: CaseStatus;
  onChange: (s: CaseStatus) => void;
}) {
  return (
    <label className={`status-select status-${status}`}>
      <span>{CASE_STATUS_LABELS[status]}</span>
      <select
        value={status}
        onChange={(e) => onChange(e.target.value as CaseStatus)}
        aria-label="Case status"
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {CASE_STATUS_LABELS[s]}
          </option>
        ))}
      </select>
    </label>
  );
}

// Small inline picker that sits beside the status pill in the case
// header. Click to open; shows existing groups, a "New group…" inline
// input, and "Remove from group". Saves immediately via the onChange
// callback. Groups are refetched every time the menu opens so another
// tab's changes show up without a page reload.
function GroupPicker({
  currentGroup,
  onChange,
}: {
  currentGroup: string | null;
  onChange: (next: string | null) => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<import("../types").CaseGroup[]>([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { groups } = await api.listGroups();
        if (!cancelled) setGroups(groups);
      } catch {
        /* best-effort */
      }
    })();
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      cancelled = true;
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function pick(next: string | null) {
    setOpen(false);
    setCreating(false);
    setNewName("");
    await onChange(next);
  }

  async function commitNew() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    await pick(trimmed);
  }

  return (
    <div
      ref={rootRef}
      className={`group-picker${open ? " open" : ""}`}
    >
      <button
        type="button"
        className={`group-picker-trigger${currentGroup ? " has-group" : " ghost"}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {currentGroup ? (
          <>
            <span className="group-picker-label">{currentGroup}</span>
            <span className="group-picker-caret" aria-hidden>▾</span>
          </>
        ) : (
          <span className="group-picker-label">+ Add to folder</span>
        )}
      </button>
      {open && (
        <div className="group-picker-menu" role="menu">
          {groups.length > 0 && (
            <div className="group-picker-section">
              {groups.map((g) => (
                <button
                  key={g.name}
                  type="button"
                  role="menuitem"
                  className={`group-picker-item${g.name === currentGroup ? " active" : ""}`}
                  onClick={() => pick(g.name)}
                >
                  <span>{g.name}</span>
                  <span className="muted">{g.count}</span>
                </button>
              ))}
            </div>
          )}
          <div className="group-picker-section">
            {creating ? (
              <form
                className="group-picker-new"
                onSubmit={(e) => {
                  e.preventDefault();
                  void commitNew();
                }}
              >
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New folder name"
                  maxLength={60}
                  autoFocus
                />
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!newName.trim()}
                >
                  Save
                </button>
              </form>
            ) : (
              <button
                type="button"
                role="menuitem"
                className="group-picker-item"
                onClick={() => setCreating(true)}
              >
                + New folder…
              </button>
            )}
            {currentGroup && (
              <button
                type="button"
                role="menuitem"
                className="group-picker-item group-picker-remove"
                onClick={() => pick(null)}
              >
                Remove from folder
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DeadlinePicker({
  value,
  onSave,
  onClear,
  onCancel,
}: {
  value: string | null;
  onSave: (d: string) => void;
  onClear: () => void;
  onCancel: () => void;
}) {
  const [date, setDate] = useState(value ?? "");
  return (
    <span className="deadline-picker">
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        autoFocus
      />
      <button
        type="button"
        className="btn-primary"
        onClick={() => date && onSave(date)}
        disabled={!date}
      >
        Save
      </button>
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
      {value && (
        <button
          type="button"
          className="btn-danger-subtle"
          onClick={onClear}
        >
          Clear
        </button>
      )}
    </span>
  );
}
